export type UserRole = 'viewer' | 'editor' | 'admin'

export interface User {
  id: string
  email: string
  name: string
  role: UserRole
  avatar?: string
  createdAt: string
  lastLogin: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  name: string
  role?: UserRole // Defaults to 'viewer'
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
  hasPermission: (permission: Permission) => boolean
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
}

// Permissions based on user roles
export type Permission = 
  | 'view_brands'      // All authenticated users
  | 'search_brands'    // All authenticated users
  | 'export_brands'    // admin only
  | 'create_brands'    // editor, admin
  | 'edit_brands'      // editor, admin
  | 'delete_brands'    // admin only
  | 'bulk_operations'  // admin only
  | 'user_management'  // admin only
  | 'system_settings'  // admin only

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  viewer: [
    'view_brands',
    'search_brands'
  ],
  editor: [
    'view_brands',
    'search_brands',
    'create_brands',
    'edit_brands'
  ],
  admin: [
    'view_brands',
    'search_brands',
    'export_brands',
    'create_brands',
    'edit_brands',
    'delete_brands',
    'bulk_operations',
    'user_management',
    'system_settings'
  ]
}

export function hasPermission(userRole: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[userRole].includes(permission)
}

export function hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
  const rolePriority: Record<UserRole, number> = {
    viewer: 1,
    editor: 2,
    admin: 3
  }
  
  return rolePriority[userRole] >= rolePriority[requiredRole]
}

export function hasAnyRole(userRole: UserRole, requiredRoles: UserRole[]): boolean {
  return requiredRoles.some(role => hasRole(userRole, role))
}

// Mock user data for development
export const MOCK_USERS: User[] = [
  {
    id: '1',
    email: 'admin@brandscope.com',
    name: 'Admin User',
    role: 'admin',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin',
    createdAt: '2024-01-01T00:00:00Z',
    lastLogin: new Date().toISOString()
  },
  {
    id: '2',
    email: 'editor@brandscope.com',
    name: 'Editor User',
    role: 'editor',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=editor',
    createdAt: '2024-01-01T00:00:00Z',
    lastLogin: new Date().toISOString()
  },
  {
    id: '3',
    email: 'viewer@brandscope.com',
    name: 'Viewer User',
    role: 'viewer',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=viewer',
    createdAt: '2024-01-01T00:00:00Z',
    lastLogin: new Date().toISOString()
  }
]

// Development credentials
export const MOCK_CREDENTIALS: Record<string, string> = {
  'admin@brandscope.com': 'admin123',
  'editor@brandscope.com': 'editor123',
  'viewer@brandscope.com': 'viewer123'
}